/*
 * aura-unlock.js: the footer's email signup (sections/footer.liquid, newsletter block).
 * One email goes two places at once:
 * - the Shopify customer form, posted in place. The form as the server renders it after the post replaces
 *   this one, and that render is the only place the footer prints the code. Shopify's bot challenge, a
 *   network failure or an unexpected answer fall back to a normal post (through Shopify's captcha wrapper),
 *   which reloads into the same state.
 * - the Marketing API's /events/user, the same call as the discount sign-up (aura-meerkat.js signup()):
 *   the id it returns becomes `mkt`, product pages seen before sign-up follow it, and `mkt_hidden` keeps
 *   the sign-up modal from opening by itself again.
 * On success the code box wipes in (the squeegee pass) and the code goes on the cart with
 * aura.applyDiscount, so checkout takes it off without typing.
 * With [data-follow-sale] the amount and code follow the sign-up modal's live sale (a holiday from
 * <api>/hollist), so both always offer the same thing.
 * The code is remembered (localStorage) so later pages show it instead of asking again; it is put on the
 * cart once per session.
 * With the sign-up modal on the page, the signup also unlocks the discount site-wide (aura-meerkat.js
 * unlock()): prices are marked down, the page reloads into them, and the modal keeps the code that fits
 * the cart on it, so this element leaves the cart alone for an unlocked visitor.
 * "Have a code?" (snippets/meerkat-code-form.liquid, outside the customer form) hands a typed code to the
 * modal (applyCode): if it's on, the page reloads into the new prices; if not, it says so and nothing changes.
 * Without the modal (shop accounts, the sign-up off) it hides, as there is nothing to apply.
 */
(() => {
  const aura = () => window.aura || {};
  const track = (n, p) => aura().track && aura().track(n, p);
  const SAVED = 'aura.unlock.v1';
  const APPLIED = 'aura.unlock.applied';
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));
  const read = (store, key) => { try { return JSON.parse(store.getItem(key)); } catch (e) { return null; } };
  const write = (store, key, value) => { try { store.setItem(key, JSON.stringify(value)); } catch (e) { /* private mode */ } };
  // The Meerkat keys hold plain strings, as the live theme wrote them
  const ls = {
    get: (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } },
    set: (k, v) => { try { localStorage.setItem(k, v); } catch (e) { /* private mode */ } },
    del: (k) => { try { localStorage.removeItem(k); } catch (e) { /* private mode */ } }
  };
  const post = (url, body) => fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

  class AuraUnlock extends HTMLElement {
    connectedCallback() {
      if (this.form) return;
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.api = String(this.dataset.api || '').replace(/\/$/, '');
      // Caught on the way down: Shopify's captcha script binds the form itself and always submits it natively
      this.addEventListener('submit', (e) => { if (e.target === this.form) this.submit(e); }, true);
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-unlock-copy]');
        if (btn) this.copy(btn);
      });
      if (this.hasAttribute('data-follow-sale')) this.followSale();
      this.codeForm = this.querySelector('[data-meerkat-promo-form]');
      if (this.codeForm) {
        this.codeForm.addEventListener('submit', (e) => this.submitCode(e));
        this.meerkat().then((m) => { if (!m || !m.applyCode) this.codeForm.closest('[data-meerkat-promo]').hidden = true; });
      }

      // Unlocked by a normal post (no JS at submit time, or the challenge page sent the shopper back), or the
      // discount is already unlocked on the cart (sections/footer.liquid)
      const code = this.shownCode();
      if (code) {
        this.remember(code);
        this.apply(code);
        // A post that went the long way round unlocks the discount here
        if (/[?&]customer_posted=true\b/.test(location.search)) {
          this.meerkat().then((m) => m && !m.unlocked && m.unlock('footer').then(() => location.reload()));
        }
        return;
      }
      const saved = read(localStorage, SAVED);
      if (saved && saved.key === this.dataset.key && saved.code) this.restore(saved.code);
    }

    /* The sign-up modal (aura-meerkat.js), when it can unlock the discount; null without one (shop accounts,
       the sign-up off). Deferred scripts run in order, so it is on the page by now if it ever will be */
    meerkat() {
      const el = document.querySelector('aura-meerkat');
      if (!el) return Promise.resolve(null);
      return customElements.whenDefined('aura-meerkat').then(() => (el.canUnlock ? el : null));
    }

    shownCode() {
      const btn = this.querySelector('[data-unlock-done] [data-unlock-copy]');
      return btn ? btn.dataset.unlockCopy : '';
    }

    /* The sign-up modal works out today's sale after loading <api>/hollist; take its amount and code */
    followSale() {
      customElements.whenDefined('aura-meerkat').then(() => {
        const meerkat = document.querySelector('aura-meerkat');
        return meerkat && meerkat.loaded && meerkat.loaded.then(() => {
          const sale = meerkat.sale;
          if (!sale || !sale.code || !sale.amount) return;
          this.sale = sale;
          this.querySelectorAll('[data-unlock-amount]').forEach((el) => { el.textContent = `$${sale.amount}`; });
          const shown = this.shownCode();
          if (shown && shown !== sale.code) this.show(sale.code, true);
        });
      }).catch(() => { /* the theme's code stands */ });
    }

    async submit(e) {
      e.preventDefault();
      e.stopPropagation();
      const form = this.form;
      if (form.getAttribute('aria-busy') === 'true') return;
      const btn = form.querySelector('[type="submit"]');
      form.setAttribute('aria-busy', 'true');
      if (btn) btn.setAttribute('aria-busy', 'true');
      const email = (form.querySelector('[name="contact[email]"]') || {}).value || '';
      const listed = this.signup(email.trim());

      let fresh = null;
      try {
        const res = await fetch(form.action, { method: 'POST', body: new FormData(form), headers: { Accept: 'text/html' } });
        if (res.ok && !/\/challenge\b/.test(new URL(res.url).pathname)) {
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          fresh = doc.getElementById(form.id);
        }
      } catch (err) { /* network: post normally below */ }
      if (!fresh) {
        // Let the Marketing API call finish first (never more than 3 seconds), as the contact form does.
        // form.submit is Shopify's wrapper when its captcha script is bound: it keeps the fields for the challenge
        await Promise.race([listed, wait(3000)]);
        form.submit();
        return;
      }

      form.innerHTML = fresh.innerHTML;
      form.removeAttribute('aria-busy');
      if (!form.querySelector('[data-unlock-done], [data-unlock-success]')) {
        // Shopify's error (bad email): it is in the swapped markup, next to the field
        const input = form.querySelector('input[type="email"]');
        if (input) input.focus();
        return;
      }
      const code = (this.sale && this.sale.code) || this.shownCode();
      track('aura_newsletter_signup', { surface: 'footer', offer: !!code });
      const meerkat = await this.meerkat();
      // A lead now: the AI try-on's gate (aura-tryon.js) lets its held render go, after the reload when one follows
      ls.set('mkt_email', 'true');
      document.dispatchEvent(new CustomEvent('aura:lead', { detail: { field: 'email', surface: 'footer', reload: !!meerkat } }));
      if (meerkat) {
        // Prices across the site are marked down for this email: reload into them (the signup then shows the code)
        if (code) this.show(code, false);
        await meerkat.unlock('footer');
        location.reload();
        return;
      }
      if (!code) return;
      this.show(code, false);
      const box = this.querySelector('[data-unlock-code-box]');
      if (box) box.classList.add('squeegee-in');
      this.apply(code, true);
    }

    /* "Have a code?": the modal checks and applies it; a code that moves prices reloads the page into them */
    async submitCode(e) {
      e.preventDefault();
      const form = e.currentTarget;
      const input = form.querySelector('input');
      const button = form.querySelector('button');
      const msg = form.querySelector('[data-meerkat-promo-msg]');
      const say = (text, tone) => {
        msg.textContent = text;
        msg.hidden = !text;
        msg.classList.toggle('is-ok', tone === 'ok');
        msg.classList.toggle('is-error', tone === 'error');
      };
      const meerkat = await this.meerkat();
      if (!meerkat || !meerkat.applyCode || button.disabled) return;
      button.disabled = true;
      say(meerkat.t.code_checking);
      const r = await meerkat.applyCode(input.value, 'footer');
      button.disabled = false;
      if (!r.ok) {
        if (!r.retry) input.setAttribute('aria-invalid', 'true');
        say(r.text, 'error');
        input.focus();
        return;
      }
      input.removeAttribute('aria-invalid');
      say(r.text, 'ok');
      if (!r.changed) return;
      // Long enough to read the answer, and the cart has the amount by then
      await Promise.all([r.done, wait(1500)]);
      location.reload();
    }

    /* The Marketing API list, as the discount sign-up joins it (aura-meerkat.js signup() and flushAnon()) */
    signup(email) {
      if (!this.api || !email) return Promise.resolve();
      ls.set('mkt_hidden', 'true');
      const id = ls.get('mkt');
      return post(`${this.api}/events/user`, id ? { id, email } : { email })
        .then((r) => r.json())
        .then((res) => {
          if (!res || !res.id) return;
          ls.set('mkt', res.id);
          let seen = null;
          try { seen = JSON.parse(ls.get('anon_mkt_list')); } catch (e) { /* none */ }
          if (!Array.isArray(seen)) return;
          ls.del('anon_mkt_list');
          seen.forEach((uri) => post(`${this.api}/events/meerkat`, { id: res.id, uri }).catch(() => {}));
        })
        .catch(() => { /* the Shopify signup still stands */ });
    }

    /* Put a code in the box and on the copy button (the sale's code can differ from the theme's) */
    show(code, applyIt) {
      const el = this.querySelector('[data-unlock-code]');
      if (el) el.textContent = code;
      const btn = this.querySelector('[data-unlock-copy]');
      if (btn) btn.dataset.unlockCopy = code;
      this.remember(code);
      if (applyIt) this.apply(code);
    }

    /* A visitor who unlocked before: show their code and the done panel without asking again */
    restore(code) {
      const tpl = this.querySelector('template[data-unlock-template]');
      const entry = this.querySelector('[data-unlock-entry]');
      if (!tpl || !entry) return;
      entry.replaceWith(tpl.content.cloneNode(true));
      tpl.remove();
      this.show((this.sale && this.sale.code) || code, true);
    }

    remember(code) {
      write(localStorage, SAVED, { code, key: this.dataset.key });
    }

    /* Put the code on the cart (once a session unless just unlocked) and say where it stands */
    async apply(code, fresh = false) {
      const status = this.querySelector('[data-unlock-status]');
      if (!fresh && read(sessionStorage, APPLIED) === code) return;
      // An unlocked visitor's codes are the sign-up modal's to keep (aura-meerkat.js syncCodes)
      const meerkat = await this.meerkat();
      if (meerkat && meerkat.unlocked) return;
      let res = null;
      try { res = aura().applyDiscount ? await aura().applyDiscount(code) : null; } catch (e) { /* cart down: copy still works */ }
      if (res && res.ok) write(sessionStorage, APPLIED, code);
      if (status) status.textContent = res && res.ok ? (res.applicable ? status.dataset.applied : status.dataset.saved) : status.dataset.manual;
      track('aura_offer_code_applied', { surface: 'footer', ok: !!(res && res.ok), applicable: !!(res && res.applicable) });
    }

    copy(btn) {
      const code = btn.dataset.unlockCopy;
      if (!code || !navigator.clipboard) return;
      navigator.clipboard.writeText(code).then(() => {
        if (aura().toast) aura().toast(btn.dataset.copied);
        track('aura_offer_code_copied', { surface: 'footer' });
      }).catch(() => {});
    }
  }

  if (!customElements.get('aura-unlock')) customElements.define('aura-unlock', AuraUnlock);
})();
